import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { Receipt, FileText, Mail, Download, Check, Eye, Printer } from 'lucide-react';
import { toast } from 'sonner';

const schoolInvoices = [
  { number: 'INV-SLF-CDS-2028-OPS-JAN-001', entity: 'Orchid Primary', amount: 396.00, date: '20 Jan 2028', status: 'paid', paidDate: '28 Jan 2028' },
  { number: 'INV-SLF-CDS-2028-ACS-JAN-001', entity: 'ACS (Independent)', amount: 412.00, date: '20 Jan 2028', status: 'pending', paidDate: null },
  { number: 'INV-SLF-CDS-2028-NYP-JAN-001', entity: 'Nanyang Primary', amount: 388.00, date: '20 Jan 2028', status: 'pending', paidDate: null },
  { number: 'INV-SLF-CDS-2028-VJC-JAN-001', entity: 'Victoria JC', amount: 352.00, date: '20 Jan 2028', status: 'paid', paidDate: '25 Jan 2028' },
  { number: 'INV-SLF-CDS-2028-HCI-JAN-001', entity: 'Hwa Chong Institution', amount: 428.00, date: '20 Jan 2028', status: 'pending', paidDate: null },
];

const ppuInvoices = [
  { number: 'INV-PPU-2028-OPS-001', school: 'Orchid Primary', month: 'Jan 2028', copies: 12, amount: 186.40, status: 'pending' },
  { number: 'INV-PPU-2028-ACS-001', school: 'ACS (Independent)', month: 'Jan 2028', copies: 8, amount: 124.80, status: 'pending' },
  { number: 'INV-PPU-2028-NJC-001', school: 'National JC', month: 'Jan 2028', copies: 15, amount: 232.50, status: 'paid' },
  { number: 'INV-PPU-2028-MGS-001', school: 'Methodist Girls\' School', month: 'Jan 2028', copies: 6, amount: 93.60, status: 'paid' },
  { number: 'INV-PPU-2028-RI-001', school: 'Raffles Institution', month: 'Jan 2028', copies: 20, amount: 312.00, status: 'pending' },
];

const ppuLineItems = [
  { title: 'Between Two Worlds', qty: 3, price: 16.80 },
  { title: 'Climate Change & You', qty: 2, price: 14.20 },
  { title: 'Code Red', qty: 2, price: 17.20 },
  { title: 'The Wonders of Coding', qty: 2, price: 18.50 },
  { title: 'The Last Library', qty: 3, price: 15.90 },
];

const mkInvoices = [
  { number: 'INV-MK-2026-001', kindergarten: 'MK @ Bendemeer', books: 20, amount: 550.00, status: 'acknowledged', acknowledgedDate: '15 Jan 2027' },
  { number: 'INV-MK-2026-002', kindergarten: 'MK @ Punggol', books: 20, amount: 550.00, status: 'acknowledged', acknowledgedDate: '14 Jan 2027' },
  { number: 'INV-MK-2026-003', kindergarten: 'MK @ Woodlands', books: 20, amount: 550.00, status: 'pending', acknowledgedDate: null },
  { number: 'INV-MK-2026-004', kindergarten: 'MK @ Tampines', books: 20, amount: 550.00, status: 'pending', acknowledgedDate: null },
];

const mkInvoicePreviewData = {
  invoiceNumber: 'INV-MK-CONSOLIDATED-2026',
  issuedTo: 'Ministry of Education HQ',
  issueDate: '20 Jan 2027',
  dueDate: '20 Feb 2027',
  items: [
    { description: "Benny's Big Adventure (EL) × 58 MKs", qty: 58, unitPrice: 12.00, total: 696.00 },
    { description: 'Kisah Si Kancil (ML) × 58 MKs', qty: 58, unitPrice: 10.50, total: 609.00 },
    { description: 'Little Tamil Tales Vol 2 (TL) × 58 MKs', qty: 58, unitPrice: 11.00, total: 638.00 },
    { description: 'Xiao Xiong De Meng (CL) × 58 MKs', qty: 58, unitPrice: 9.90, total: 574.20 },
    { description: 'The Magic Seed (EL) × 58 MKs', qty: 58, unitPrice: 13.50, total: 783.00 },
    { description: 'Processing Fee (20 books × 58 MKs × $3)', qty: 1160, unitPrice: 3.00, total: 3480.00 },
  ],
  subtotal: 6780.20,
  total: 31900.00
};

const pilotInvoices = [
  { number: 'INV-PILOT-AUG', amount: 12480, month: 'Aug 2026', status: 'paid' },
  { number: 'INV-PILOT-SEP', amount: 12480, month: 'Sep 2026', status: 'paid' },
  { number: 'INV-PILOT-OCT', amount: 12480, month: 'Oct 2026', status: 'paid' },
  { number: 'INV-PILOT-CAT-C1', amount: 4200, month: 'End Cycle 1', status: 'paid' },
  { number: 'INV-PILOT-OCLC', amount: 1200, month: 'One-time', status: 'paid' },
  { number: 'INV-PILOT-SPINE', amount: 800, month: 'One-time', status: 'paid' },
  { number: 'INV-PILOT-NOV', amount: 12480, month: 'Nov 2026', status: 'awaiting' },
];

export default function CivicaInvoicing() {
  const [mkPreviewOpen, setMkPreviewOpen] = useState(false);

  const handleBulkGenerate = () => {
    toast.success('All pending invoices generated');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <Receipt className="w-7 h-7 text-[#2E5F8A]" />
          Invoicing & Payment Management
        </h1>
      </div>

      <Tabs defaultValue="school">
        <TabsList>
          <TabsTrigger value="school">CDS School Invoices</TabsTrigger>
          <TabsTrigger value="ppu">Pay-Per-Use</TabsTrigger>
          <TabsTrigger value="mk">MK Invoices</TabsTrigger>
          <TabsTrigger value="pilot">Pilot Invoices</TabsTrigger>
        </TabsList>

        {/* CDS School Invoices Tab */}
        <TabsContent value="school" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">CDS School Invoices</CardTitle>
                <CardDescription>Triggered when school acknowledges delivery</CardDescription>
              </div>
              <Button className="bg-[#2E5F8A]" onClick={handleBulkGenerate} data-testid="bulk-generate-invoices">
                <FileText className="w-4 h-4 mr-2" />
                Bulk Generate All Pending Invoices
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schoolInvoices.map((inv, idx) => (
                    <TableRow key={idx} className="table-row-striped">
                      <TableCell className="font-mono text-xs">{inv.number}</TableCell>
                      <TableCell>{inv.entity}</TableCell>
                      <TableCell>${inv.amount.toFixed(2)}</TableCell>
                      <TableCell>{inv.date}</TableCell>
                      <TableCell>
                        {inv.status === 'paid' ? (
                          <Badge className="bg-emerald-50 text-emerald-700">✓ Paid {inv.paidDate}</Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700">⏳ Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost">
                            <Eye className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost">
                            <Mail className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost">
                            <Download className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Invoice Preview */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="font-heading text-base">Invoice Preview — INV-SLF-CDS-2028-OPS-JAN-001</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p><strong>To:</strong> Orchid Primary | <strong>From:</strong> Civica | <strong>PO:</strong> PO-OPS-CDS-C1-2028</p>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between py-1">
                    <span>"The Wonders of Coding" (Sarah Chen) × 1</span>
                    <span>$18.50</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span>"Climate Change & You" (David Atkins) × 2 (add'l)</span>
                    <span>$28.40</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span>"Between Two Worlds" (Amelia Tan) × 1</span>
                    <span>$16.80</span>
                  </div>
                  <div className="flex justify-between py-1 text-slate-500">
                    <span>+ 17 more lines...</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between py-1">
                      <span>Books sub-total</span>
                      <span>$336.00</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span>Aggregated processing (20 × $3.00)</span>
                      <span>$60.00</span>
                    </div>
                    <div className="flex justify-between py-1 font-bold">
                      <span>TOTAL</span>
                      <span>$396.00</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm">
                  <FileText className="w-4 h-4 mr-2" />
                  Preview PDF
                </Button>
                <Button variant="outline" size="sm">
                  <Mail className="w-4 h-4 mr-2" />
                  Email to Finance
                </Button>
                <Button size="sm" className="bg-emerald-600">
                  <Check className="w-4 h-4 mr-2" />
                  Mark Sent
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pay-Per-Use Tab */}
        <TabsContent value="ppu" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Pay-Per-Use Invoices</CardTitle>
                <CardDescription>Additional copies ordered by schools beyond CDS allocation</CardDescription>
              </div>
              <Button className="bg-[#2E5F8A]" onClick={handleBulkGenerate}>
                <FileText className="w-4 h-4 mr-2" />
                Generate Monthly PPU Invoices
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead>Add'l Copies</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ppuInvoices.map((inv, idx) => (
                    <TableRow key={idx} className="table-row-striped">
                      <TableCell className="font-mono text-xs">{inv.number}</TableCell>
                      <TableCell>{inv.school}</TableCell>
                      <TableCell>{inv.month}</TableCell>
                      <TableCell>{inv.copies}</TableCell>
                      <TableCell>${inv.amount.toFixed(2)}</TableCell>
                      <TableCell>
                        {inv.status === 'paid' ? (
                          <Badge className="bg-emerald-50 text-emerald-700">✓ Paid</Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700">⏳ Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost">
                            <Eye className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost">
                            <Mail className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost">
                            <Download className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* PPU Invoice Preview */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="font-heading text-base">PPU Invoice Preview — INV-PPU-2028-OPS-001</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p><strong>To:</strong> Orchid Primary | <strong>Period:</strong> Jan 2028 | <strong>Type:</strong> Pay-Per-Use Additional Copies</p>
                <div className="border-t pt-2 mt-2">
                  {ppuLineItems.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-1">
                      <span>"{item.title}" × {item.qty}</span>
                      <span>${(item.price * item.qty).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between py-1">
                      <span>Books sub-total (12 copies)</span>
                      <span>$186.40</span>
                    </div>
                    <div className="flex justify-between py-1 text-slate-500">
                      <span>Processing fee waived (PPU)</span>
                      <span>$0.00</span>
                    </div>
                    <div className="flex justify-between py-1 font-bold">
                      <span>TOTAL</span>
                      <span>$186.40</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm">
                  <FileText className="w-4 h-4 mr-2" />
                  Preview PDF
                </Button>
                <Button variant="outline" size="sm">
                  <Mail className="w-4 h-4 mr-2" />
                  Email to Finance
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MK Invoices Tab */}
        <TabsContent value="mk" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">MK Programme Invoices</CardTitle>
                <CardDescription>Annual consolidated invoice issued to MOE HQ after ALL 58 MKs acknowledge</CardDescription>
              </div>
              <Button 
                className="bg-[#2E5F8A]" 
                onClick={() => setMkPreviewOpen(true)}
                data-testid="preview-mk-invoice"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Consolidated Invoice
              </Button>
            </CardHeader>
            <CardContent>
              {/* Status Banner */}
              <div className="p-4 bg-amber-50 rounded-lg mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-amber-800">Invoice Generation Status</p>
                    <p className="text-sm text-amber-700">42 of 58 MKs have acknowledged delivery. 16 remaining.</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 px-4 py-2">On Hold</Badge>
                </div>
                <div className="mt-3 h-2 bg-amber-200 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500" style={{ width: '72%' }} />
                </div>
              </div>

              {/* MK List */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice Ref</TableHead>
                    <TableHead>Kindergarten</TableHead>
                    <TableHead>Books</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Acknowledged</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mkInvoices.map((inv, idx) => (
                    <TableRow key={idx} className="table-row-striped">
                      <TableCell className="font-mono text-xs">{inv.number}</TableCell>
                      <TableCell>{inv.kindergarten}</TableCell>
                      <TableCell>{inv.books}</TableCell>
                      <TableCell>${inv.amount.toFixed(2)}</TableCell>
                      <TableCell>
                        {inv.status === 'acknowledged' ? (
                          <Badge className="bg-emerald-50 text-emerald-700">✓ Acknowledged</Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700">⏳ Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {inv.acknowledgedDate || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4 text-center">Showing 4 of 58 MKs</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pilot Invoices Tab */}
        <TabsContent value="pilot" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Pilot Programme Invoices</CardTitle>
              <CardDescription>Monthly invoices for pilot schools</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pilotInvoices.map((inv, idx) => (
                    <TableRow key={idx} className="table-row-striped">
                      <TableCell className="font-mono text-xs">{inv.number}</TableCell>
                      <TableCell>${inv.amount.toLocaleString()}</TableCell>
                      <TableCell>{inv.month}</TableCell>
                      <TableCell>
                        {inv.status === 'paid' ? (
                          <Badge className="bg-emerald-50 text-emerald-700">✓ Paid</Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700">⏳ Awaiting ack</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* MK Invoice Preview Modal */}
      <Dialog open={mkPreviewOpen} onOpenChange={setMkPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">MK Programme — Consolidated Invoice Preview</DialogTitle>
            <DialogDescription>
              This invoice will be generated once all 58 MKs acknowledge delivery
            </DialogDescription>
          </DialogHeader>

          {/* Invoice Preview */}
          <div className="border rounded-lg p-6 bg-white mt-4">
            {/* Invoice Header */}
            <div className="flex justify-between items-start mb-6 pb-4 border-b">
              <div>
                <h3 className="font-bold text-lg">INVOICE</h3>
                <p className="text-sm text-slate-500">{mkInvoicePreviewData.invoiceNumber}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-[#2E5F8A]">Civica Pte Ltd</p>
                <p className="text-sm text-slate-500">CDS Programme Office</p>
                <p className="text-sm text-slate-500">Singapore</p>
              </div>
            </div>

            {/* Bill To / Dates */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-xs text-slate-500 uppercase">Bill To</p>
                <p className="font-semibold">{mkInvoicePreviewData.issuedTo}</p>
                <p className="text-sm text-slate-500">1 North Buona Vista Drive</p>
                <p className="text-sm text-slate-500">Singapore 138675</p>
              </div>
              <div className="text-right">
                <p className="text-sm"><span className="text-slate-500">Issue Date:</span> {mkInvoicePreviewData.issueDate}</p>
                <p className="text-sm"><span className="text-slate-500">Due Date:</span> {mkInvoicePreviewData.dueDate}</p>
                <p className="text-sm"><span className="text-slate-500">Programme:</span> MK 2026</p>
              </div>
            </div>

            {/* Line Items */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mkInvoicePreviewData.items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right">{item.qty}</TableCell>
                    <TableCell className="text-right">${item.unitPrice.toFixed(2)}</TableCell>
                    <TableCell className="text-right">${item.total.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Totals */}
            <div className="border-t mt-4 pt-4">
              <div className="flex justify-end">
                <div className="w-64">
                  <div className="flex justify-between py-1">
                    <span className="text-slate-500">Subtotal</span>
                    <span>${mkInvoicePreviewData.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-slate-500">GST (9%)</span>
                    <span>Inclusive</span>
                  </div>
                  <div className="flex justify-between py-2 border-t font-bold text-lg">
                    <span>Total</span>
                    <span className="text-[#2E5F8A]">${mkInvoicePreviewData.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t text-sm text-slate-500">
              <p>Payment Terms: Net 30 days</p>
              <p>Bank: DBS Bank | Account: 012-345678-9 | Payee: Civica Pte Ltd</p>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button variant="outline">
              <Printer className="w-4 h-4 mr-2" />
              Print Preview
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button className="bg-[#2E5F8A]" disabled>
              <Mail className="w-4 h-4 mr-2" />
              Send to MOE HQ (16 MKs pending)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
